import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Wallet, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  TrendingUp,
  Calendar,
  User,
  Phone,
  Mail,
  AlertTriangle,
  Banknote,
  CreditCard,
  ArrowUpRight,
  Search
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RestaurantDeposits() {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'cancelled', 'stats'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Refund modal
  const [refundModal, setRefundModal] = useState({ open: false, order: null });
  const [refundReason, setRefundReason] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  
  // Mark collected modal
  const [collectModal, setCollectModal] = useState({ open: false, order: null });
  const [submittingCollect, setSubmittingCollect] = useState(false);

  const fetchDashboard = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/deposit/dashboard`);
      setDashboardData(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleMarkCollected = async () => {
    if (!collectModal.order) return;
    setSubmittingCollect(true);
    try {
      await axios.post(
        `${API}/restaurant-dashboard/deposit/mark-collected/${collectModal.order.id}`,
        {}
      );
      toast.success('Paiement marqué comme encaissé');
      setCollectModal({ open: false, order: null });
      fetchDashboard();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la mise à jour'));
    } finally {
      setSubmittingCollect(false);
    }
  };

  const handleRequestRefund = async () => {
    if (!refundModal.order || !refundReason.trim()) return;
    setSubmittingRefund(true);
    try {
      const response = await axios.post(
        `${API}/restaurant-dashboard/deposit/refund/${refundModal.order.id}?reason=${encodeURIComponent(refundReason)}`,
        {}
      );
      toast.success(response.data.message || 'Remboursement effectué avec succès');
      setRefundModal({ open: false, order: null });
      setRefundReason('');
      fetchDashboard();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors du remboursement'));
    } finally {
      setSubmittingRefund(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPickupInfo = (order) => {
    if (order.pickup_date && order.pickup_date !== new Date().toISOString().split('T')[0]) {
      return `${new Date(order.pickup_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} à ${order.pickup_slot || ''}`;
    }
    return order.pickup_slot || '-';
  };

  // Filter orders based on search
  const filteredPendingOrders = dashboardData?.pending_orders?.filter(order => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.client?.name?.toLowerCase().includes(search)
    );
  }) || [];

  const filteredCancelledOrders = dashboardData?.cancelled_orders?.filter(order => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.client?.name?.toLowerCase().includes(search)
    );
  }) || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet className="w-7 h-7 text-amber-500" />
              Tableau de bord des acomptes
            </h1>
            <p className="text-gray-500 mt-1">Gérez les acomptes et paiements en attente</p>
          </div>
          <Button
            onClick={() => fetchDashboard(true)}
            variant="outline"
            disabled={refreshing}
            className="rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-100 rounded-xl">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-gray-500">En attente</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{dashboardData?.pending_count || 0}</p>
            <p className="text-sm text-gray-500 mt-1">
              {dashboardData?.pending_remaining_to_collect?.toFixed(2) || '0.00'}€ à encaisser
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-gray-500">Ce mois</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              {dashboardData?.statistics?.month_deposits?.toFixed(2) || '0.00'}€
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {dashboardData?.statistics?.month_count || 0} commandes
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">Aujourd'hui</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {dashboardData?.statistics?.today_deposits?.toFixed(2) || '0.00'}€
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {dashboardData?.statistics?.today_count || 0} commandes
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Banknote className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-gray-500">Total encaissé</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {dashboardData?.statistics?.total_deposits_collected?.toFixed(2) || '0.00'}€
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {dashboardData?.statistics?.total_orders_with_deposit || 0} commandes
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'pending'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            En attente ({dashboardData?.pending_count || 0})
          </button>
          <button
            onClick={() => setActiveTab('cancelled')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'cancelled'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <XCircle className="w-4 h-4 inline mr-2" />
            Annulées ({dashboardData?.cancelled_count || 0})
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par n° de commande ou nom client..."
            className="pl-12 rounded-xl"
          />
        </div>

        {/* Pending Orders Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {filteredPendingOrders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Aucune commande en attente</h3>
                <p className="text-gray-500">
                  Toutes les commandes avec acompte ont été finalisées.
                </p>
              </div>
            ) : (
              filteredPendingOrders.map(order => (
                <div
                  key={order.id}
                  className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-gray-900">{order.order_number}</span>
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                          Acompte payé
                        </Badge>
                        {order.is_reservation && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                            Réservation
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {order.client?.name || 'Client'}
                        </span>
                        {order.client?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {order.client.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          Retrait: {formatPickupInfo(order)}
                        </span>
                      </div>

                      {/* Items preview */}
                      {order.items && (
                        <p className="text-sm text-gray-600 mt-2">
                          {order.items.map(i => `${i.quantity}x ${i.dish_name}`).join(', ')}
                        </p>
                      )}
                    </div>

                    {/* Payment Info */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">Acompte encaissé</p>
                        <p className="font-semibold text-emerald-600 flex items-center gap-1 justify-end">
                          <CreditCard className="w-4 h-4" />
                          {order.deposit_amount?.toFixed(2)}€
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">Reste à encaisser</p>
                        <p className="font-bold text-xl text-amber-600 flex items-center gap-1 justify-end">
                          <Banknote className="w-5 h-5" />
                          {order.remaining_amount?.toFixed(2)}€
                        </p>
                      </div>
                      <Button
                        onClick={() => setCollectModal({ open: true, order })}
                        className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Encaisser
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Cancelled Orders Tab */}
        {activeTab === 'cancelled' && (
          <div className="space-y-4">
            {filteredCancelledOrders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Aucune commande annulée</h3>
                <p className="text-gray-500">
                  Aucune commande avec acompte n'a été annulée.
                </p>
              </div>
            ) : (
              filteredCancelledOrders.map(order => (
                <div
                  key={order.id}
                  className="bg-white rounded-2xl border border-red-100 p-5"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-gray-900">{order.order_number}</span>
                        <Badge className="bg-red-100 text-red-700 border-red-200">
                          Annulée
                        </Badge>
                        {order.refund_completed && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Remboursé
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {order.client?.name || 'Client'}
                        </span>
                        {order.client?.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {order.client.email}
                          </span>
                        )}
                        <span>Annulée le {formatDate(order.cancelled_at)}</span>
                      </div>

                      {order.cancellation_reason && (
                        <p className="text-sm text-red-600 mt-2">
                          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                          Raison: {order.cancellation_reason}
                        </p>
                      )}
                      
                      {order.refund_completed && order.refund_reason && (
                        <p className="text-sm text-emerald-600 mt-2">
                          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                          Remboursé le {formatDate(order.refund_completed_at)} - {order.refund_reason}
                        </p>
                      )}
                    </div>

                    {/* Payment Info */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">
                          {order.refund_completed ? 'Acompte remboursé' : 'Acompte payé'}
                        </p>
                        <p className={`font-bold text-lg ${order.refund_completed ? 'text-emerald-600' : 'text-red-600'}`}>
                          {order.deposit_amount?.toFixed(2)}€
                        </p>
                      </div>
                      {!order.refund_completed ? (
                        <Button
                          onClick={() => setRefundModal({ open: true, order })}
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                        >
                          <ArrowUpRight className="w-4 h-4 mr-2" />
                          Rembourser
                        </Button>
                      ) : (
                        <div className="text-sm text-emerald-600 bg-emerald-50 px-4 py-2 rounded-xl flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Remboursé
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Collect Payment Modal */}
      <Dialog open={collectModal.open} onOpenChange={(open) => !open && setCollectModal({ open: false, order: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Confirmer l'encaissement
            </DialogTitle>
          </DialogHeader>
          
          {collectModal.order && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Commande</p>
                <p className="font-bold">{collectModal.order.order_number}</p>
                <p className="text-sm text-gray-500 mt-2">Client</p>
                <p className="font-medium">{collectModal.order.client?.name || 'Client'}</p>
              </div>
              
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-sm text-emerald-600 mb-1">Montant à encaisser sur place</p>
                <p className="text-3xl font-bold text-emerald-700">
                  {collectModal.order.remaining_amount?.toFixed(2)}€
                </p>
              </div>
              
              <p className="text-sm text-gray-500">
                En confirmant, vous indiquez que le client a payé le solde de sa commande sur place.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCollectModal({ open: false, order: null })}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button
              onClick={handleMarkCollected}
              disabled={submittingCollect}
              className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
            >
              {submittingCollect ? 'Traitement...' : 'Confirmer l\'encaissement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={refundModal.open} onOpenChange={(open) => !open && setRefundModal({ open: false, order: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-red-500" />
              Rembourser l'acompte
            </DialogTitle>
          </DialogHeader>
          
          {refundModal.order && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Commande</p>
                <p className="font-bold">{refundModal.order.order_number}</p>
                <p className="text-sm text-gray-500 mt-2">Client</p>
                <p className="font-medium">{refundModal.order.client?.name || 'Client'}</p>
                <p className="text-sm">{refundModal.order.client?.email}</p>
              </div>
              
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-600 mb-1">Montant à rembourser</p>
                <p className="text-3xl font-bold text-red-700">
                  {refundModal.order.deposit_amount?.toFixed(2)}€
                </p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  Le remboursement sera effectué <strong>immédiatement</strong> sur le moyen de paiement du client. Cette action est irréversible.
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Raison du remboursement *
                </label>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Ex: Commande annulée à la demande du client plus de 48h avant le retrait..."
                  className="rounded-xl"
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRefundModal({ open: false, order: null }); setRefundReason(''); }}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button
              onClick={handleRequestRefund}
              disabled={submittingRefund || !refundReason.trim()}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {submittingRefund ? 'Remboursement en cours...' : 'Confirmer le remboursement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
